import React, { useState, useEffect } from "react";
import clubService from "../../services/clubService";
import userService from "../../services/userService";
import { useNotification } from "../../context/NotificationContext";

const AddClubMember = ({ club, onClose, onRefresh }) => {
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedRole, setSelectedRole] = useState("MEMBER");
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const { showSuccess, showError } = useNotification();

  useEffect(() => {
    if (!club?.campusId) return;

    const debounceTimer = setTimeout(async () => {
      try {
        setLoading(true);

        // Use the club's campus ID to filter users from the same campus
        const response = await userService.getUsersForClubMembership(
          club.campusId,
          page,
          10,
          search
        );

        if (response.success && response.data) {
          setUsers(response.data.content || []);
          setTotalPages(response.data.totalPages || 0);
        } else {
          setUsers([]);
          setTotalPages(0);
        }
      } catch (error) {
        console.error("Error fetching users:", error);
        showError("Failed to fetch available users");
        setUsers([]);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [club?.campusId, page, search, showError]);

  const handleAddMember = async () => {
    if (!selectedUser) {
      showError("Please select a user");
      return;
    }

    try {
      setLoading(true);
      const response = await clubService.addMember(club.id, {
        userId: selectedUser.id,
        role: selectedRole,
      });

      if (response.success) {
        showSuccess("Member added successfully!");
        setSelectedUser(null);
        setSelectedRole("MEMBER");
        onRefresh();
        onClose();
      }
    } catch (error) {
      console.error("Error adding member:", error);
      showError("Failed to add member");
    } finally {
      setLoading(false);
    }
  };

  const getUserStatusBadge = (user) => {
    const isBanned = user.banned === true || user.isBanned === true;
    const isActive = user.active === true || user.isActive === true;
    const isEmailVerified = user.emailVerified === true;

    if (isBanned) {
      return (
        <span className="badge bg-danger bg-opacity-10 text-danger">
          Banned
        </span>
      );
    }
    if (!isActive) {
      return (
        <span className="badge bg-warning bg-opacity-10 text-warning">
          Inactive
        </span>
      );
    }
    if (!isEmailVerified) {
      return (
        <span className="badge bg-info bg-opacity-10 text-info">
          Unverified
        </span>
      );
    }
    return (
      <span className="badge bg-success bg-opacity-10 text-success">
        Active
      </span>
    );
  };

  return (
    <div className="add-club-member">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h5 className="mb-0">
          <i className="fas fa-user-plus me-2"></i>
          Add New Member
        </h5>
        <button className="btn btn-outline-secondary btn-sm" onClick={onClose}>
          <i className="fas fa-times"></i>
        </button>
      </div>

      <div className="mb-4">
        <div className="input-group">
          <span className="input-group-text">
            <i className="fas fa-search"></i>
          </span>
          <input
            type="text"
            className="form-control"
            placeholder="Search users by name or email..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0); // Reset to first page when searching
            }}
          />
        </div>
      </div>

      {loading ? (
        <div className="text-center py-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : users.length > 0 ? (
        <div className="table-responsive">
          <table className="table table-hover align-middle">
            <thead className="bg-light">
              <tr>
                <th className="fw-bold">User</th>
                <th className="fw-bold">Institution</th>
                <th className="fw-bold">Status</th>
                <th className="fw-bold">Role</th>
                <th className="fw-bold text-center">Action</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id}>
                  <td>
                    <div className="d-flex align-items-center">
                      <div
                        className="bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center me-3"
                        style={{ width: "40px", height: "40px" }}
                      >
                        <i className="fas fa-user text-primary"></i>
                      </div>
                      <div>
                        <div className="fw-bold">{user.fullName}</div>
                        <small className="text-muted">{user.email}</small>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div>
                      <div className="fw-bold">{user.universityName}</div>
                      <small className="text-muted">{user.campusName}</small>
                    </div>
                  </td>
                  <td>{getUserStatusBadge(user)}</td>
                  <td>
                    <select
                      className="form-select form-select-sm"
                      value={selectedUser?.id === user.id ? selectedRole : ""}
                      onChange={(e) => {
                        setSelectedUser(user);
                        setSelectedRole(e.target.value);
                      }}
                      style={{ width: "120px" }}
                    >
                      <option value="">Select Role</option>
                      <option value="ADMIN">Admin</option>
                      <option value="MODERATOR">Moderator</option>
                      <option value="MEMBER">Member</option>
                    </select>
                  </td>
                  <td className="text-center">
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={handleAddMember}
                      disabled={
                        selectedUser?.id !== user.id ||
                        !selectedRole ||
                        user.banned === true ||
                        user.isBanned === true ||
                        !(user.active === true || user.isActive === true)
                      }
                      title={
                        user.banned === true || user.isBanned === true
                          ? "Cannot add banned user"
                          : !(user.active === true || user.isActive === true)
                          ? "Cannot add inactive user"
                          : "Add member"
                      }
                    >
                      <i className="fas fa-plus me-1"></i>
                      Add
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-5">
          <i
            className="fas fa-users text-muted mb-3"
            style={{ fontSize: "3rem" }}
          ></i>
          <h6 className="text-muted">No Users Found</h6>
          <p className="text-muted">Try adjusting your search criteria.</p>
        </div>
      )}

      {totalPages > 1 && (
        <div className="d-flex justify-content-center mt-4">
          <nav>
            <ul className="pagination">
              <li className={`page-item ${page === 0 ? "disabled" : ""}`}>
                <button
                  className="page-link"
                  onClick={() => setPage(page - 1)}
                  disabled={page === 0}
                >
                  Previous
                </button>
              </li>
              <li className="page-item">
                <span className="page-link">
                  Page {page + 1} of {totalPages}
                </span>
              </li>
              <li
                className={`page-item ${
                  page === totalPages - 1 ? "disabled" : ""
                }`}
              >
                <button
                  className="page-link"
                  onClick={() => setPage(page + 1)}
                  disabled={page === totalPages - 1}
                >
                  Next
                </button>
              </li>
            </ul>
          </nav>
        </div>
      )}
    </div>
  );
};

export default AddClubMember;
